import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePrograms } from '../context/ProgramsContext';
import api from '../utils/api';
import { PageLoad, ErrorMsg, SelectDrop } from '../components/ui';
import PandaDocModal from '../components/PandaDocModal';
import ProspectImport from '../components/ProspectImport';
import HostContactEditor from '../components/HostContactEditor';
import './HostProspectsPage.css';

/* ── Icons ─────────────────────────────────────────────── */
const Ico = ({ d, size = 18, stroke = 1.75 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    {typeof d === 'string' ? <path d={d} /> : d}
  </svg>
);
const PlusIcon   = ({ size = 16 }) => <Ico size={size} d="M12 5v14M5 12h14" />;
const PhoneIcon  = ({ size = 13 }) => <Ico size={size} d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.3 1.8.6 2.6a2 2 0 0 1-.5 2.1L8 9.6a16 16 0 0 0 6 6l1.2-1.2a2 2 0 0 1 2.1-.5c.8.3 1.7.5 2.6.6a2 2 0 0 1 1.7 2z" />;
const MailIcon   = ({ size = 13 }) => <Ico size={size} d={<g><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 7l9 6 9-6" /></g>} />;
const GlobeIcon  = ({ size = 13 }) => <Ico size={size} d={<g><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a15 15 0 0 1 0 18a15 15 0 0 1 0-18z" /></g>} />;
const PinIcon    = ({ size = 12 }) => <Ico size={size} d={<g><path d="M12 22s7-7.5 7-13a7 7 0 1 0-14 0c0 5.5 7 13 7 13z" /><circle cx="12" cy="9" r="2.5" /></g>} />;
const NoteIcon   = ({ size = 13 }) => <Ico size={size} d={<g><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="9" y1="13" x2="15" y2="13" /><line x1="9" y1="17" x2="13" y2="17" /></g>} />;
const CloseIcon  = ({ size = 18 }) => <Ico size={size} d="M6 6l12 12M18 6L6 18" />;
const CheckIcon  = ({ size = 14 }) => <Ico size={size} d="M20 6L9 17l-5-5" />;
const DocIcon    = ({ size = 14 }) => <Ico size={size} d={<g><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></g>} />;
const ArrowIcon  = ({ size = 13 }) => <Ico size={size} d="M5 12h14M13 6l6 6-6 6" />;
const SearchIcon = () => <Ico size={18} d={<g><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" /></g>} />;

const fmtDate = d => d ? new Date(d).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' }) : null;
const fmtDateTime = d => d ? new Date(d).toLocaleString('en-CA', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : null;

/* ── Main Page ─────────────────────────────────────────── */
export default function HostProspectsPage() {
  const [searchParams] = useSearchParams();
  const { programs: allPrograms, programMap: PROGRAM_NAMES } = usePrograms();
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [programFilter, setProgramFilter] = useState(searchParams.get('program') || '');
  const [cityFilter, setCityFilter] = useState(searchParams.get('city') || '');
  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [showRemoved, setShowRemoved] = useState(false);

  const fetchProspects = useCallback(async () => {
    try {
      const res = await api.get('/hosts/prospects', { params: { removed: showRemoved } });
      setProspects(res.data);
      setError('');
    } catch { setError('Failed to load prospects'); }
    finally { setLoading(false); }
  }, [showRemoved]);

  useEffect(() => { fetchProspects(); }, [fetchProspects]);
  useEffect(() => {
    setProgramFilter(searchParams.get('program') || '');
    setCityFilter(searchParams.get('city') || '');
  }, [searchParams]);

  const programOpts = useMemo(() => {
    const inUse = [...new Set([...prospects.flatMap(p => p.programs_accepted || []), ...(programFilter ? [programFilter] : [])])];
    return [
      { value: '', label: 'All programs' },
      ...inUse.sort().map(c => ({ value: c, label: PROGRAM_NAMES[c] || c })),
    ];
  }, [prospects, PROGRAM_NAMES, programFilter]);

  const filtered = useMemo(() => prospects.filter(p => {
    if (programFilter && !(p.programs_accepted || []).includes(programFilter)) return false;
    if (cityFilter && (p.city || '').toLowerCase() !== cityFilter.toLowerCase()) return false;
    if (!search) return true;
    const hay = [p.org_name, p.city, p.contact_first_name, p.contact_last_name].filter(Boolean).join(' ').toLowerCase();
    return hay.includes(search.toLowerCase());
  }), [prospects, search, programFilter, cityFilter]);

  const uncontacted = prospects.filter(p => !p.last_call_at).length;
  const withPrograms = prospects.filter(p => (p.programs_accepted || []).length > 0).length;

  const stats = [
    { eyebrow: 'PROSPECTS', value: prospects.length, label: showRemoved ? 'Removed from pipeline' : 'In the pipeline' },
    { eyebrow: 'NOT YET CALLED', value: uncontacted, label: 'Awaiting first contact' },
    { eyebrow: 'PROGRAM-MATCHED', value: withPrograms, label: 'Tagged to a program' },
  ];

  if (loading) return <PageLoad />;

  return (
    <>
      {/* Hero */}
      <div className="h-hero">
        <div className="h-hero-decor" aria-hidden="true">
          <svg viewBox="0 0 800 320" preserveAspectRatio="xMaxYMid slice" width="100%" height="100%">
            <g fill="none" stroke="rgba(245,166,35,0.10)" strokeWidth="1">
              <circle cx="720" cy="160" r="80" /><circle cx="720" cy="160" r="140" />
              <circle cx="720" cy="160" r="200" /><circle cx="720" cy="160" r="260" />
            </g>
          </svg>
        </div>
        <div className="h-hero-inner">
          <div className="h-hero-top">
            <div>
              <div className="h-hero-crumb">
                <span>Host Specialist</span><span className="h-crumb-sep">/</span><span>Prospects</span>
              </div>
              <h1 className="h-hero-title">Host <span className="h-hero-accent">Prospects</span></h1>
              <p className="h-hero-lede">
                Track potential partners before an agreement — log your calls, keep notes, and convert the good ones into hosts.
              </p>
            </div>
            <div className="h-hero-actions">
              <button className="h-btn-accent" onClick={() => setShowImport(s => !s)}>
                Import Spreadsheet
              </button>
              <button className="h-btn-accent" onClick={() => setShowForm(s => !s)}>
                <PlusIcon /> Add Prospect
              </button>
            </div>
          </div>

          <div className="h-stats">
            {stats.map((s, i) => (
              <div key={i} className="h-stat-card">
                <div className="h-stat-top"><span className="h-stat-eyebrow">{s.eyebrow}</span></div>
                <div className="h-stat-num">{s.value}</div>
                <div className="h-stat-label">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="h-content">
        <div className="h-content-inner">
          {error && <ErrorMsg>{error}</ErrorMsg>}
          {showImport && <ProspectImport onImported={fetchProspects} onClose={() => setShowImport(false)} />}

          {showForm && (
            <ProspectForm
              PROGRAM_NAMES={PROGRAM_NAMES}
              programs={allPrograms.map(p => p.code)}
              onSave={() => { setShowForm(false); fetchProspects(); }}
              onCancel={() => setShowForm(false)}
            />
          )}

          <div className="h-filter-bar">
            <div className="h-search-wrap">
              <SearchIcon />
              <input
                type="text"
                className="h-search-input"
                placeholder="Search prospects by name, contact or city…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              {search && (
                <button className="h-search-clear" onClick={() => setSearch('')} aria-label="Clear">
                  <CloseIcon size={14} />
                </button>
              )}
              <kbd className="h-search-kbd">⌘K</kbd>
            </div>
            <div className="h-filter-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                <input type="checkbox" checked={showRemoved} onChange={e => {
                  setShowRemoved(e.target.checked); setOpenId(null);
                }} />
                Removed prospects
              </label>
              {cityFilter && (
                <button className="btn btn-secondary" onClick={() => setCityFilter('')} aria-label={`Clear city filter: ${cityFilter}`}>
                  City: {cityFilter} · Clear
                </button>
              )}
              <SelectDrop value={programFilter} onChange={setProgramFilter} options={programOpts} minWidth={150} />
            </div>
          </div>

          <div className="h-section-meta">
            <div className="h-count">
              Showing <strong>{filtered.length}</strong> of <strong>{prospects.length}</strong> prospects
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="pros-empty">
              <div className="pros-empty-icon"><NoteIcon size={26} /></div>
              <p>{search || programFilter || cityFilter ? 'No prospects match these filters.'
                : showRemoved ? 'No removed prospects.' : 'No prospects yet.'}</p>
              {!search && !showRemoved && <span>Add one manually, or use the Host Finder to discover businesses and add them here.</span>}
            </div>
          ) : (
            <div className="pros-grid">
              {filtered.map(p => (
                <ProspectCard key={p.id} p={p} PROGRAM_NAMES={PROGRAM_NAMES} onOpen={setOpenId} />
              ))}
            </div>
          )}
        </div>
      </div>

      {openId && (
        <ProspectPanel
          key={openId}
          id={openId}
          allPrograms={allPrograms}
          PROGRAM_NAMES={PROGRAM_NAMES}
          onClose={() => setOpenId(null)}
          onChanged={fetchProspects}
        />
      )}
    </>
  );
}

/* ── Prospect Card (preview) ───────────────────────────── */
function ProspectCard({ p, PROGRAM_NAMES, onOpen }) {
  const contactName = [p.contact_first_name, p.contact_last_name].filter(Boolean).join(' ');
  const lastCall = fmtDate(p.last_call_at);

  return (
    <article className="pros-card" onClick={() => onOpen(p.id)}>
      <header className="pros-card-head">
        <div className="pros-card-title">
          <h3>{p.org_name}</h3>
          {p.city && <div className="pros-card-loc"><PinIcon /> {p.city}</div>}
        </div>
        <span className="pros-badge">Prospect</span>
      </header>

      {(contactName || p.contact_position) && (
        <div className="pros-card-contact">
          {contactName || 'Contact TBD'}
          {p.contact_position && <span className="pros-card-role"> · {p.contact_position}</span>}
        </div>
      )}

      <div className="pros-card-chan">
        {p.contact_phone && <span className="pros-chip"><PhoneIcon /> {p.contact_phone}</span>}
        {p.contact_email && <span className="pros-chip"><MailIcon /> {p.contact_email}</span>}
        {p.website && <span className="pros-chip"><GlobeIcon /> Website</span>}
        {!p.contact_phone && !p.contact_email && !p.website && <span className="pros-chip muted">No contact info</span>}
      </div>

      {(p.programs_accepted || []).length > 0 && (
        <div className="pros-card-programs">
          {p.programs_accepted.map(c => <span key={c} className="program-pill">{PROGRAM_NAMES[c] || c}</span>)}
        </div>
      )}

      {/* Latest note / call preview */}
      {p.last_note_body || p.last_note_kind ? (
        <div className="pros-card-note">
          <span className={`pros-note-tag ${p.last_note_kind === 'call' ? 'call' : 'note'}`}>
            {p.last_note_kind === 'call' ? <><PhoneIcon size={11} /> Call</> : <><NoteIcon size={11} /> Note</>}
          </span>
          <span className="pros-note-body">{p.last_note_body || 'Call logged — no details'}</span>
        </div>
      ) : (
        <div className="pros-card-note empty">No notes yet — open to log a call.</div>
      )}

      <footer className="pros-card-foot">
        <span className="pros-card-meta">
          {p.note_count > 0 ? `${p.note_count} ${p.note_count === 1 ? 'entry' : 'entries'}` : 'No activity'}
          {lastCall && ` · last call ${lastCall}`}
        </span>
        <button className="pros-open-btn" onClick={e => { e.stopPropagation(); onOpen(p.id); }}>
          Open <ArrowIcon />
        </button>
      </footer>
    </article>
  );
}

/* ── Detail Slide-over ─────────────────────────────────── */
function ProspectPanel({ id, allPrograms, PROGRAM_NAMES, onClose, onChanged }) {
  const navigate = useNavigate();
  const [host, setHost] = useState(null);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composer, setComposer] = useState('');
  const [saving, setSaving] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [showPandaDoc, setShowPandaDoc] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setErr('');
    try {
      const [h, n] = await Promise.all([
        api.get(`/hosts/${id}`),
        api.get(`/hosts/${id}/notes`),
      ]);
      setHost(h.data);
      setNotes(n.data);
    } catch {
      setErr('Couldn’t load this prospect. Check your connection and try again.');
    }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const handler = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const addNote = async (kind) => {
    const body = composer.trim();
    if (kind === 'note' && !body) return;
    setSaving(true); setErr('');
    try {
      const res = await api.post(`/hosts/${id}/notes`, { kind, body });
      setNotes(prev => [res.data, ...prev]);
      setComposer('');
      onChanged?.();
    } catch (e) {
      setErr(e.response?.data?.error || 'Couldn’t save that note — please try again.');
    }
    finally { setSaving(false); }
  };

  const promote = async () => {
    setPromoting(true); setErr('');
    try {
      const res = await api.post(`/hosts/${id}/promote`);
      setHost(res.data);
      onChanged?.();
    } catch (e) {
      setErr(e.response?.data?.error || 'Couldn’t convert this prospect — please try again.');
    }
    finally { setPromoting(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Remove "${host.org_name}" from the prospect list? Its contact details, notes and history will be preserved. You can restore it from Removed prospects. No active host or agreement will be deleted.`)) return;
    setRemoving(true); setErr('');
    try {
      await api.delete(`/hosts/prospects/${id}`);
      onChanged?.();
      onClose();
    } catch (e) {
      setErr(e.response?.data?.error || 'Could not remove this prospect.');
    } finally { setRemoving(false); }
  };
  const restore = async () => {
    setRemoving(true); setErr('');
    try {
      await api.post(`/hosts/prospects/${id}/restore`);
      onChanged?.();
      onClose();
    } catch (e) {
      setErr(e.response?.data?.error || 'Could not restore this prospect.');
    } finally { setRemoving(false); }
  };

  const contactName = host ? [host.contact_first_name, host.contact_last_name].filter(Boolean).join(' ') : '';
  const isProspect = host?.is_prospect;
  const isRemoved = Boolean(host?.prospect_removed_at);

  return (
    <>
      <div className="dp-scrim" onClick={onClose} />
      <aside className="dp" role="dialog" aria-label={host?.org_name || 'Prospect'}>
        <div className="dp-head">
          <button className="dp-close" onClick={onClose} aria-label="Close"><CloseIcon /></button>
          {loading ? (
            <div style={{ color: 'rgba(255,255,255,0.5)', paddingTop: 32, fontSize: 13 }}>Loading…</div>
          ) : host ? (
            <>
              <div className="dp-eyebrow"><PinIcon /><span>{host.city || host.campus_region || '—'}</span></div>
              <h2 className="dp-title">{host.org_name}</h2>
              <div className="dp-status">
                <div className="status-pill" style={{ background: isProspect ? 'rgba(245,166,35,0.22)' : 'rgba(31,138,91,0.25)', color: 'white' }}>
                  <span className="status-dot" style={{ background: isProspect ? '#F5A623' : '#1F8A5B' }} />
                  <span>{isRemoved ? 'Removed prospect' : isProspect ? 'Prospect' : 'Converted to host'}</span>
                </div>
              </div>
            </>
          ) : null}
        </div>

        {!loading && !host && (
          <div className="dp-body">
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--ink-3)' }}>
              <p style={{ marginBottom: 12 }}>{err || 'This prospect could not be loaded.'}</p>
              <button className="dp-btn" onClick={load}>Retry</button>
            </div>
          </div>
        )}

        {!loading && host && (
          <div className="dp-body">
            {err && (
              <div style={{ margin: '0 0 12px', padding: '8px 12px', background: '#FBE7E5', color: '#B3261E', borderRadius: 8, fontSize: 12.5 }}>
                {err}
              </div>
            )}
            {/* Convert / agreement actions */}
            <section className="dp-section">
              <h4>{isProspect ? 'Pipeline' : 'Agreement'}</h4>
              {isRemoved ? (
                <div className="pn-convert">
                  <p>This prospect was removed from the working list. Contact details and history are preserved.</p>
                  <button className="dp-btn" disabled={removing} onClick={restore}>
                    {removing ? 'Restoring…' : 'Restore prospect'}
                  </button>
                </div>
              ) : isProspect ? (
                <div className="pn-convert">
                  <p>Ready to move forward? Convert this prospect into a host, then send the site agreement.</p>
                  <button className="pn-convert-btn" disabled={promoting || removing} onClick={promote}>
                    <CheckIcon /> {promoting ? 'Converting…' : 'Convert to host'}
                  </button>
                  <button className="dp-btn" style={{ color: '#b42318', marginTop: 12 }}
                    disabled={removing || promoting} onClick={remove}>
                    {removing ? 'Removing…' : 'Remove prospect'}
                  </button>
                </div>
              ) : (
                <div className="pn-convert done">
                  <p><CheckIcon size={15} /> This prospect is now an active host.</p>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button className="dp-btn" onClick={() => setShowPandaDoc(true)}>
                      <DocIcon /> Send agreement
                    </button>
                    <button className="dp-btn" onClick={() => navigate(`/hosts/${host.id}`)}>
                      <ArrowIcon /> Open host record
                    </button>
                  </div>
                </div>
              )}
            </section>

            {/* Contact */}
            <section className="dp-section">
              <h4>Contact</h4>
              <div className="pn-contact">
                {contactName && (
                  <div className="pn-contact-name">
                    {contactName}
                    {host.contact_position && <span className="pn-role"> · {host.contact_position}</span>}
                  </div>
                )}
                {host.contact_phone && <a className="pn-line" href={`tel:${host.contact_phone}`}><PhoneIcon /> {host.contact_phone}</a>}
                {host.contact_email && <a className="pn-line" href={`mailto:${host.contact_email}`}><MailIcon /> {host.contact_email}</a>}
                {host.website && <a className="pn-line" href={host.website} target="_blank" rel="noreferrer"><GlobeIcon /> {host.website.replace(/^https?:\/\//, '')}</a>}
                {host.address && <div className="pn-line muted"><PinIcon /> {[host.address, host.city, host.province].filter(Boolean).join(', ')}</div>}
                {!contactName && !host.contact_phone && !host.contact_email && !host.website && (
                  <div className="pn-line muted">No contact details on file.</div>
                )}
              </div>
              <HostContactEditor key={host.id} host={host} onSaved={() => { load(); onChanged?.(); }} />
            </section>

            {/* Programs */}
            {(host.programs_accepted || []).length > 0 && (
              <section className="dp-section">
                <h4>Programs of interest</h4>
                <div className="dp-programs">
                  {host.programs_accepted.map(c => <span key={c} className="program-pill">{PROGRAM_NAMES[c] || c}</span>)}
                </div>
              </section>
            )}

            {/* Notes & call log */}
            <section className="dp-section">
              <h4>Notes &amp; call log</h4>

              <div className="pn-composer">
                <textarea
                  className="pn-textarea"
                  placeholder="What happened on the call, or a general note…"
                  value={composer}
                  onChange={e => setComposer(e.target.value)}
                  rows={3}
                />
                <div className="pn-composer-actions">
                  <button className="pn-btn call" disabled={saving} onClick={() => addNote('call')} title="Log that you made a call">
                    <PhoneIcon /> Log call
                  </button>
                  <button className="pn-btn note" disabled={saving || !composer.trim()} onClick={() => addNote('note')}>
                    <NoteIcon /> Add note
                  </button>
                </div>
              </div>

              {notes.length === 0 ? (
                <div className="pn-empty">No calls or notes recorded yet.</div>
              ) : (
                <ul className="pn-timeline">
                  {notes.map(n => (
                    <li key={n.id} className={`pn-item ${n.kind}`}>
                      <span className="pn-dot">{n.kind === 'call' ? <PhoneIcon size={12} /> : <NoteIcon size={12} />}</span>
                      <div className="pn-item-body">
                        <div className="pn-item-head">
                          <strong>{n.kind === 'call' ? 'Call logged' : 'Note'}</strong>
                          <span className="pn-item-meta">{n.author_name || 'Staff'} · {fmtDateTime(n.created_at)}</span>
                        </div>
                        {n.body && <div className="pn-item-text">{n.body}</div>}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        )}
      </aside>

      {showPandaDoc && host && (
        <PandaDocModal
          host={host}
          programs={allPrograms.filter(p => (host.programs_accepted || []).includes(p.code))}
          onClose={() => setShowPandaDoc(false)}
          onSent={() => { setShowPandaDoc(false); load(); onChanged?.(); }}
        />
      )}
    </>
  );
}

/* ── Add Prospect Form ─────────────────────────────────── */
function ProspectForm({ programs, PROGRAM_NAMES, onSave, onCancel }) {
  const [form, setForm] = useState({
    org_name: '', contact_first_name: '', contact_last_name: '', contact_position: '',
    contact_email: '', contact_phone: '', website: '', address: '', city: '', province: '',
    programs_accepted: [], notes: '',
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.org_name.trim()) { setError('Organization name is required'); return; }
    setSaving(true);
    try {
      await api.post('/hosts/prospects', form);
      onSave();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add prospect');
      setSaving(false);
    }
  };

  const toggleProgram = (p) => setForm(prev => ({
    ...prev,
    programs_accepted: prev.programs_accepted.includes(p)
      ? prev.programs_accepted.filter(x => x !== p)
      : [...prev.programs_accepted, p],
  }));

  return (
    <div className="card card-pad pros-form">
      <div className="pros-form-title">Add a Prospect</div>
      {error && <ErrorMsg>{error}</ErrorMsg>}
      <form onSubmit={submit} className="pros-form-grid">
        <PField label="Organization Name" value={form.org_name} onChange={v => setForm({ ...form, org_name: v })} required span />
        <PField label="First Name" value={form.contact_first_name} onChange={v => setForm({ ...form, contact_first_name: v })} />
        <PField label="Last Name" value={form.contact_last_name} onChange={v => setForm({ ...form, contact_last_name: v })} />
        <PField label="Position" value={form.contact_position} onChange={v => setForm({ ...form, contact_position: v })} placeholder="e.g. Owner" />
        <PField label="Phone" value={form.contact_phone} onChange={v => setForm({ ...form, contact_phone: v })} />
        <PField label="Email" type="email" value={form.contact_email} onChange={v => setForm({ ...form, contact_email: v })} />
        <PField label="Website" value={form.website} onChange={v => setForm({ ...form, website: v })} placeholder="https://" />
        <PField label="City" value={form.city} onChange={v => setForm({ ...form, city: v })} placeholder="e.g. Calgary" />
        <PField label="Province" value={form.province} onChange={v => setForm({ ...form, province: v })} placeholder="e.g. Alberta" />
        <PField label="Street Address" value={form.address} onChange={v => setForm({ ...form, address: v })} span />
        <div className="pros-form-full">
          <label className="pros-flabel">Programs of interest</label>
          <div className="pros-prog-row">
            {programs.map(p => (
              <label key={p} className="pros-prog-check">
                <input type="checkbox" checked={form.programs_accepted.includes(p)} onChange={() => toggleProgram(p)} />
                {PROGRAM_NAMES[p] || p}
              </label>
            ))}
          </div>
        </div>
        <div className="pros-form-full">
          <label className="pros-flabel">Initial note (optional)</label>
          <textarea className="input" rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="How you found them, first impressions…" />
        </div>
        <div className="pros-form-actions">
          <button type="button" className="btn btn-ghost" onClick={onCancel}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Adding…' : 'Add Prospect'}</button>
        </div>
      </form>
    </div>
  );
}

function PField({ label, value, onChange, type = 'text', required = false, placeholder = '', span = false }) {
  return (
    <div style={span ? { gridColumn: '1 / -1' } : undefined}>
      <label className="pros-flabel">{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} className="input" required={required} placeholder={placeholder} />
    </div>
  );
}
